import { UserContextProvider } from '@context/user'
import { lazy, StrictMode, Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import './index.css'
// Importar analytics - esto inicializará Google Analytics automáticamente NO QUITAR
import '../utils/analytics'

if (import.meta.env.DEV) {
  import('@utils/debug/compareLinkedItemMaterials')
  import('@utils/i18nRepro').then(({ registerI18nReproDevTools }) => registerI18nReproDevTools())
}

// Lazy loading de componentes de rutas principales
const Home = lazy(() => import('@components/Home').then((m) => ({ default: m.Home })))
const Login = lazy(() => import('@components/Login').then((m) => ({ default: m.Login })))
const Register = lazy(() => import('@components/Register').then((m) => ({ default: m.Register })))
const EditProject = lazy(() =>
  import('@components/EditProject').then((m) => ({ default: m.EditProject })),
)
const Planner = lazy(() => import('@components/Planner').then((m) => ({ default: m.Planner })))
const RoomBuilder = lazy(() =>
  import('@components/RoomBuilder').then((m) => ({ default: m.RoomBuilder })),
)

// Lazy loading de componentes IA
const IAForm = lazy(() => import('@components/IA/IaForm').then((m) => ({ default: m.IAForm })))
const IA = lazy(() => import('@components/IA').then((m) => ({ default: m.IA })))
const IaGen = lazy(() => import('@components/IA/iaGen').then((m) => ({ default: m.IaGen })))
const SpinnerIA = lazy(() =>
  import('@components/Loaders/spinnerIA').then((m) => ({ default: m.SpinnerIA })),
)

// Lazy loading de modales y componentes secundarios
const DuplicateProject = lazy(() =>
  import('@components/DuplicateProject/DuplicateProject').then((m) => ({
    default: m.DuplicateProject,
  })),
)
const FileUploader = lazy(() => import('@components/FileConverter/fileUploader'))
const DuplicateConfirm = lazy(() =>
  import('@components/GenericModals/CopyProjectModal').then((m) => ({
    default: m.DuplicateConfirm,
  })),
)
const DoorModal = lazy(() => import('@components/GenericModals/DoorModal'))
const ErrorModal = lazy(() => import('@components/GenericModals/ErrorModal'))
const FavoriteModal = lazy(() =>
  import('@components/GenericModals/FavoriteModal').then((m) => ({ default: m.FavoriteModal })),
)
const LogoutModal = lazy(() => import('@components/GenericModals/LogoutModal'))
const OpenProjectModal = lazy(() =>
  import('@components/GenericModals/OpenProjectModal').then((m) => ({
    default: m.OpenProjectModal,
  })),
)
const USureModal = lazy(() => import('@components/GenericModals/USureModal'))
const WindowModal = lazy(() => import('@components/GenericModals/WindowModal'))

// Componentes de información y estadísticas
const Information = lazy(() =>
  import('@components/Information').then((m) => ({ default: m.Information })),
)
const ListOfElements = lazy(() =>
  import('@components/ListOfElements/ListOfElements').then((m) => ({ default: m.ListOfElements })),
)
const Statistics = lazy(() =>
  import('@components/Statistics').then((m) => ({ default: m.Statistics })),
)

// Componentes de loaders y modales de confirmación
const Spinner = lazy(() =>
  import('@components/Loaders/spinner').then((m) => ({ default: m.Spinner })),
)
const ModalConfirm = lazy(() =>
  import('@components/ModalConfirm').then((m) => ({ default: m.ModalConfirm })),
)
const SaveConfirm = lazy(() =>
  import('@components/ModalConfirm/SaveConfirm').then((m) => ({ default: m.SaveConfirm })),
)
const SaveWrong = lazy(() =>
  import('@components/ModalConfirm/SaveWrong').then((m) => ({ default: m.SaveWrong })),
)
const NewDesign = lazy(() =>
  import('@components/ModalConfirm/newDesign').then((m) => ({ default: m.NewDesign })),
)
const ItemEditor = lazy(() => import('@components/ModalEditor/UniversalEditor/ItemEditor'))
const PredefinedRooms = lazy(() =>
  import('@components/PredefinedRooms').then((m) => ({ default: m.PredefinedRooms })),
)
const SaveProject = lazy(() => import('@components/Topbar/SaveProject'))
const PageVisor = lazy(() => import('@components/VR').then((m) => ({ default: m.PageVisor })))
const TemporaryOpenProject = lazy(() => import('./TemporaryOpenProject'))

// Componente de fallback para Suspense
const LoadingFallback = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      fontSize: '1.2rem',
      color: '#666',
    }}
  >
    Cargando...
  </div>
)

// export { Outlet }

const router = createBrowserRouter(
  [
    {
    path: '/iaform',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <IAForm />
      </Suspense>
    ),
    children: [
      {
        path: 'spinner',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SpinnerIA msg='MSG_286' />
          </Suspense>
        ),
      },
      {
        path: 'imgIAGen',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <IaGen />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/ia',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <IA />
      </Suspense>
    ),
  },

  {
    path: '/',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Home />
      </Suspense>
    ),
    children: [
      {
        path: 'login',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Login href='/proyectos' />
          </Suspense>
        ),
      },
      {
        path: 'register',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Register />
          </Suspense>
        ),
      },
      {
        path: 'proyectos',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <EditProject />
          </Suspense>
        ),
      },
      {
        path: 'deleteProyect/:idVersiones',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <USureModal />
          </Suspense>
        ),
      },
      {
        path: 'estancias',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <PredefinedRooms />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/planner/list',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ListOfElements />
      </Suspense>
    ),
  },
  {
    path: '/planner/statistics',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Statistics />
      </Suspense>
    ),
  },
  // { path: '/planner/pdf', element: <PdfView /> },

  {
    path: '/planner',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Planner />
      </Suspense>
    ),
    loader: () => {
      //TODO: mover lógica de cosas al loader!!
      // AÑadir: las texturas, toda la info del usuario blablabla
      // las preferencias, las imágenes iniciales
      return null
    },
    children: [
      // { path: '/planner/pdf', element: <PdfView /> },
      {
        path: '/planner/duplicated',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DuplicateConfirm />
          </Suspense>
        ),
      },
      {
        path: '/planner/confirm',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ModalConfirm text='Hola' />
          </Suspense>
        ),
      },
      {
        path: '/planner/create/:action',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RoomBuilder />
          </Suspense>
        ),
      },
      {
        path: '/planner/login',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Login href='/planner' />
          </Suspense>
        ),
      },
      {
        path: '/planner/register',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Register />
          </Suspense>
        ),
      },
      {
        path: '/planner/oksave',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SaveConfirm />
          </Suspense>
        ),
      },
      {
        path: '/planner/nosave',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SaveWrong />
          </Suspense>
        ),
      },
      {
        path: '/planner/newDesign',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <NewDesign />
          </Suspense>
        ),
      },
      {
        path: '/planner/proyectos',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <EditProject />
          </Suspense>
        ),
      },
      // { path: '/planner/list', element: <ListOfElements /> },
      {
        path: '/planner/list/textureEdit',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ItemEditor />
          </Suspense>
        ),
      },

      {
        path: '/planner/copyProject/:idVersiones',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DuplicateProject />
          </Suspense>
        ),
      },
      {
        path: '/planner/door/:id',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DoorModal msg='MSG_212' />
          </Suspense>
        ),
      },
      {
        path: '/planner/window/:id',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <WindowModal msg='MSG_217' />
          </Suspense>
        ),
      },
      {
        path: '/planner/openProject/:idVersiones',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <OpenProjectModal />
          </Suspense>
        ),
      },
      {
        path: '/planner/confirmlogout',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LogoutModal />
          </Suspense>
        ),
      },
      {
        path: '/planner/register/error/:errorText',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ErrorModal />
          </Suspense>
        ),
      },
      {
        path: '/planner/login/error/:errorText',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ErrorModal />
          </Suspense>
        ),
      },
      {
        path: '/planner/proyectos/error/:errorText',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ErrorModal />
          </Suspense>
        ),
      },
      {
        path: '/planner/downloading',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Spinner msg='MSG_215' />
          </Suspense>
        ),
      },
      {
        path: '/planner/seivo',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Spinner msg='MSG_272' />
          </Suspense>
        ),
      },
      {
        path: '/planner/generateHDR',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Spinner msg='MSG_302' />
          </Suspense>
        ),
      },
      {
        path: '/planner/generateGLB',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Spinner msg='MSG_331' />
          </Suspense>
        ),
      },
      {
        path: '/planner/deleteProyect/:idVersiones',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <USureModal />
          </Suspense>
        ),
      },
      {
        path: '/planner/url/:id',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <TemporaryOpenProject />
          </Suspense>
        ),
      },
      {
        path: '/planner/info',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Information />
          </Suspense>
        ),
      },
      {
        path: '/planner/saveProject',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SaveProject />
          </Suspense>
        ),
      },
      {
        path: '/planner/fileUploader',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FileUploader />
          </Suspense>
        ),
      },
      {
        path: '/planner/setfav',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FavoriteModal />
          </Suspense>
        ),
      },
      {
        path: '/planner/visor/:idDisenos',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <PageVisor />
          </Suspense>
        ),
      },
      // { path: '/planner/hdr', element: <HDR /> },
    ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
    },
  },
)

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <UserContextProvider>
      <RouterProvider
        router={router}
        future={{
          v7_startTransition: true,
        }}
      />
    </UserContextProvider>
  </StrictMode>,
)
